import React from "react"
import {useTranslation} from "react-i18next"
import {StarIcon as StarIconOutline, TrashIcon} from "@heroicons/react/24/outline"
import {LinkIcon, StarIcon as StarIconSolid} from "@heroicons/react/24/solid"
import {twMerge} from "tailwind-merge"

import {EmailCell, PhoneNumberCell} from "../../../components/Anonymized.tsx"
import {IconButton} from "../../../components/Button.tsx"
import {DnclWarning} from "../../../components/DnclWarning.tsx"
import {Enriched} from "../../../components/Enriched.tsx"
import {EnrichmentFailed} from "../../../components/EnrichmentFailed.tsx"
import {EnumFilterContent} from "../../../components/EnumFilter.tsx"
import {TOption} from "../../../components/formElements/Dropdown/types"
import {Link} from "../../../components/Link.tsx"
import {Loading} from "../../../components/Loading.tsx"
import {Table} from "../../../components/Table/Table.tsx"
import {TableNoRows} from "../../../components/Table/TableNoRows.tsx"
import {TableRow, TTableRowOnClick} from "../../../components/Table/TableRow.tsx"
import {getColumnsFromMeta} from "../../../components/Table/utils/columns.ts"
import {rowCheckColumn, TRowCheckingProps} from "../../../components/Table/utils/rowChecking.tsx"
import {TColumnsMetaWithEmpty, TOrderBy} from "../../../components/Table/utils/shared.ts"
import {useLSTableColumnsState} from "../../../components/Table/utils/useLSTableColumnsState.tsx"
import {Tooltip} from "../../../components/Tooltip.tsx"
import {useDeprioritizeProspectMutation, usePrioritizeProspectMutation} from "../../../queries/prospects.ts"
import {useUserSettingsOrLogout} from "../../../queries/user.ts"
import requestError from "../../../services/requestError.tsx"
import {
  AAdminProspect,
  APhoneFieldDnclStatusEnum,
  AProspectApprovalStatuses,
  AProspectAutoEnrichmentStatuses,
  AProspectDisapproveReasons,
  ASaasCompanyUserProspect,
  ASalesPersonProspect,
} from "../../../services/types.generated.ts"
import {addHttpToURL, getFullName} from "../../../utils"
import {getWorstDnclStatus} from "../../../utils/dncl.ts"
import {enumTranslKey} from "../../../utils/i18n.tsx"
import {isAdminUser} from "../../../utils/types.ts"
import {DeletingContext, EditingContext} from "./context.ts"
import {RejectedReason} from "./RejectedReason.tsx"
import {Warnings} from "./Warnings.tsx"

type TTableProspect = AAdminProspect | ASaasCompanyUserProspect | ASalesPersonProspect

export type TAdminTableColumn =
  | "actions"
  | "warnings"
  | "company"
  | "website"
  | "owner"
  | "segment"
  | "city"
  | "contact person"
  | "position"
  | "phone"
  | "email"
  | "rejected reason"
  | "crm"
  | "enrichment_status"

const tableId = "prospects/base-table"

type TSegmentFilter = {
  availableSegments: string[]
  selectedSegment: string | null
  onSegmentChange: (segment: string | null) => void
}

export const DataTable: React.FC<
  {
    data: TTableProspect[]
    isLoading: boolean
    isPrioritizeActionVisible?: boolean
    orderBy: TOrderBy<TAdminTableColumn>
    setOrderBy: (newOrderBy: TOrderBy<TAdminTableColumn>) => void
    refetch: () => void
    isReadOnly?: boolean
    leadAgentEnabled: boolean
    autoEnrichmentEnabled: boolean
    segmentFilter?: TSegmentFilter
  } & TRowCheckingProps
> = ({
  data,
  isLoading,
  isPrioritizeActionVisible,
  isReadOnly,
  leadAgentEnabled,
  autoEnrichmentEnabled,
  setOrderBy,
  orderBy,
  refetch,
  checkRow,
  checkedRows,
  segmentFilter,
}) => {
  const {t} = useTranslation()

  const {user} = useUserSettingsOrLogout()

  const editContext = EditingContext.useContext()
  const deleteContext = DeletingContext.useContext()

  const prioritizeMutation = usePrioritizeProspectMutation()
  const deprioritizeMutation = useDeprioritizeProspectMutation()

  const handleTogglePriority = React.useCallback(
    async (prospect: TTableProspect) => {
      try {
        await (prospect.prioritized ? deprioritizeMutation : prioritizeMutation).mutateAsync(prospect.id)
        refetch()
      } catch (e) {
        requestError(e)
      }
    },
    [deprioritizeMutation, prioritizeMutation, refetch]
  )

  const hasWarnings = data.some(row => !!row.warnings?.length)
  const hasDnclWarnings = data.some(row => {
    const status = getWorstDnclStatus(row)
    return status === APhoneFieldDnclStatusEnum.Listed || status === APhoneFieldDnclStatusEnum.Failed
  })
  const isAdmin = isAdminUser(user)
  const segmentFilterOptions = React.useMemo(() => {
    if (!segmentFilter) {
      return undefined
    }

    return segmentFilter.availableSegments.map(segment => ({
      value: segment,
      title: t(enumTranslKey("Segment", segment), segment),
      key: segment,
    })) satisfies Array<TOption<string>>
  }, [segmentFilter, t])

  const selectedSegmentOption = React.useMemo(() => {
    if (!segmentFilter || !segmentFilterOptions) {
      return null
    }

    return segmentFilterOptions.find(option => option.value === segmentFilter.selectedSegment) ?? null
  }, [segmentFilter, segmentFilterOptions])

  const columnsMeta = React.useMemo<TColumnsMetaWithEmpty<TAdminTableColumn, TTableProspect>>(
    () => [
      !isReadOnly && rowCheckColumn,
      !isReadOnly && {
        column: "actions",
        size: "min-content",
        HeaderCellValue: () => t("Prospects_Table_Actions"),
        CellValue: ({row}) => {
          return (
            <div className={"flex gap-4"}>
              {isPrioritizeActionVisible && (
                <Tooltip
                  buttonNode={
                    <IconButton
                      className={"text-cr-black hover:text-cr-blue"}
                      noEdges
                      onClick={() => handleTogglePriority(row)}
                    >
                      {row.prioritized ? (
                        <StarIconSolid className={"size-4 text-cr-blue"} />
                      ) : (
                        <StarIconOutline className={"size-4"} />
                      )}
                    </IconButton>
                  }
                  openDelay={200}
                >
                  <div className={"rounded-lg bg-cr-black px-4 py-2 text-sm text-cr-white"}>
                    {row.prioritized ? t("Prospects_Table_RemovePriorityMark") : t("Prospects_Table_MarkAsPriority")}
                  </div>
                </Tooltip>
              )}
              <Tooltip
                buttonNode={
                  <IconButton
                    className={"text-cr-black hover:text-cr-red"}
                    noEdges
                    onClick={() => deleteContext.setValue.call(null, row)}
                  >
                    <TrashIcon className={"size-4"} />
                  </IconButton>
                }
                openDelay={200}
              >
                <div className={"rounded-lg bg-cr-black px-4 py-2 text-sm text-cr-white"}>
                  {t("Prospects_Table_Delete")}
                </div>
              </Tooltip>
            </div>
          )
        },
      },
      data.some(row => !!row.disapprove_reason) && {
        column: "rejected reason",
        size: "min-content",
        HeaderCellValue: () => null,
        CellValue: ({row}) => (
          <RejectedReason
            reason={
              row.disapprove_reason === AProspectDisapproveReasons.Other
                ? row.disapprove_reason_text
                : row.disapprove_reason
            }
          />
        ),
      },
      !isReadOnly &&
        ((isAdmin && hasWarnings) || hasDnclWarnings) && {
          column: "warnings",
          size: "min-content",
          HeaderCellValue: () => null,
          CellValue: ({row}) => (
            <div className={"flex gap-1"}>
              {isAdmin && <Warnings warnings={row.warnings} prospectId={row.id} />}
              <DnclWarning entity={row} />
            </div>
          ),
        },
      data.some(row => row.crm_synced) && {
        column: "crm",
        size: "min-content",
        align: "center",
        HeaderCellValue: () => t("Prospects_Table_CRM"),
        CellValue: ({row}) => (row.crm_synced ? <LinkIcon className={"h-4 w-4 text-cr-black"} /> : null),
      },
      autoEnrichmentEnabled && {
        column: "enrichment_status",
        size: "max-content",
        align: "center",
        HeaderCellValue: () => t("Prospects_Table_EnrichmentStatus"),
        CellValue: ({row}) => {
          if (row.auto_enrichment_status == AProspectAutoEnrichmentStatuses.InProgress) {
            return (
              <span>
                <Loading size={"sm"} fullSpace={false} delayShow={true} />
              </span>
            )
          } else if (row.auto_enrichment_status == AProspectAutoEnrichmentStatuses.Enriched) {
            return (
              <span>
                <Enriched size={"sm"} fullSpace={false} />
              </span>
            )
          } else {
            return (
              <span>
                <EnrichmentFailed size={"sm"} fullSpace={false} />
              </span>
            )
          }
        },
      },
      "added_by" in (data[0] ?? {}) && {
        column: "owner",
        HeaderCellValue: () => t("Prospects_Table_Owner"),
        CellValue: ({row}) => ("added_by" in row ? row.added_by : null),
      },
      {
        column: "company",
        size: "max-content",
        sortFn: true,
        HeaderCellValue: () => t("Prospects_Table_Company"),
        CellValue: ({row}) => <span>{row.organization_name ?? "-"}</span>,
      },
      {
        column: "website",
        HeaderCellValue: () => t("Prospects_Table_Website"),
        CellValue: ({row}) =>
          row.website ? (
            <Link to={addHttpToURL(row.website)} target={"_blank"} flipUnderline>
              {row.website}
            </Link>
          ) : (
            "-"
          ),
      },
      {
        column: "segment",
        size: "max-content",
        HeaderCellValue: () => t("Prospects_Table_Segment"),
        FilterContent:
          segmentFilter && segmentFilterOptions
            ? () => (
                <EnumFilterContent
                  options={segmentFilterOptions}
                  multiple={false}
                  value={selectedSegmentOption}
                  onChange={option => segmentFilter.onSegmentChange(option?.value ?? null)}
                />
              )
            : undefined,
        isFiltered: () => !!segmentFilter?.selectedSegment,
        clearFilter: () => segmentFilter?.onSegmentChange(null),
        CellValue: ({row}) => (
          <span>{row.segment?.map(segment => t(enumTranslKey("Segment", segment), segment)).join(", ") ?? "-"}</span>
        ),
      },
      {
        column: "city",
        size: "max-content",
        sortFn: true,
        HeaderCellValue: () => t("Prospects_Table_City"),
        CellValue: ({row}) => <span>{[row.city, row.country?.name].filter(Boolean).join(", ") || "-"}</span>,
      },
      {
        column: "contact person",
        size: "max-content",
        HeaderCellValue: () => t("Prospects_Table_ContactPerson"),
        CellValue: ({row}) => (
          <span>
            {getFullName({first_name: row.contact_person_first_name, last_name: row.contact_person_last_name}) || "-"}
          </span>
        ),
      },
      {column: "position", sortFn: true, HeaderCellValue: () => t("Prospects_Table_Position")},
      leadAgentEnabled && {
        column: "phone",
        size: "max-content",
        HeaderCellValue: () => t("Prospects_Table_Phone"),
        CellValue: PhoneNumberCell,
      },
      leadAgentEnabled && {
        column: "email",
        sortFn: true,
        HeaderCellValue: () => t("Prospects_Table_Email"),
        CellValue: EmailCell,
      },
    ],
    [
      data,
      deleteContext.setValue,
      handleTogglePriority,
      hasDnclWarnings,
      hasWarnings,
      isAdmin,
      isPrioritizeActionVisible,
      isReadOnly,
      leadAgentEnabled,
      autoEnrichmentEnabled,
      segmentFilter,
      segmentFilterOptions,
      selectedSegmentOption,
      t,
    ]
  )

  const handleClick = React.useCallback<TTableRowOnClick<TTableProspect>>(
    row => {
      editContext.setValue.call(null, row)
    },
    [editContext.setValue]
  )

  return (
    <Table<TAdminTableColumn, TTableProspect>
      {...useLSTableColumnsState(tableId, {columnsOrder: getColumnsFromMeta(columnsMeta), pinnedColumn: null})}
      data={data}
      loading={isLoading}
      orderBy={orderBy}
      onOrder={setOrderBy}
      checkedRows={checkedRows}
      onCheckRow={checkRow}
      columnsMeta={columnsMeta}
    >
      {({data, pinnedColumn}) => (
        <>
          {data.length ? (
            data.map((row, index) => (
              <TableRow
                key={row.id}
                onClick={handleClick}
                isClickDisabled={isReadOnly}
                row={row}
                rowIndex={index}
                className={twMerge([
                  row.id === editContext.value?.id && "bg-cr-blue-super-light",
                  !!row.warnings?.length && isAdmin && "text-cr-yellow",
                  row.approval_status === AProspectApprovalStatuses.Disapproved && "text-cr-red",
                ])}
                pinnedColumn={pinnedColumn}
              />
            ))
          ) : (
            <TableNoRows>{t("Prospects_NoProspects_TableCaption")}</TableNoRows>
          )}
        </>
      )}
    </Table>
  )
}
